import React, { memo, useRef, useState, useMemo, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
// import { Designer } from '@c2s-npm/plumreact';
import useAsset from '../hooks/useAsset';
import { lazyRetry } from '@/utils/lazyRetry';
import ErrorBoundary from '@/components/tailwind/ErrorBoundary';

const Designer = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "DefaultDesigner" */ '../../customization/components/DefaultDesigner'),
    'DefaultDesigner'
  )
);

const DesignExporter = ({ success, failure }, forwardRef) => {
  const { asset } = useAsset();

  const designerRef = useRef();
  const intervalRef = useRef();
  const intervalStartRef = useRef();
  const { details, design: rawDesign, miyo } = useSelector((state) => state.pdpItem);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // deep copy the design, something is mutating it in designer and react state is getting angry
  const design = useMemo(() => JSON.parse(JSON.stringify(rawDesign)), [rawDesign]);

  const submit = () => {
    if (!!asset) {
      // lazy load the designer and then start polling for when it is loaded
      setSubmitting(true);
      intervalStartRef.current = Date.now();
      intervalRef.current = setInterval(() => {
        if (!!designerRef.current && design && config) {
          designerRef.current.submit();
          clearInterval(intervalRef.current);
        } else if (Date.now() - intervalStartRef.current > 5 * 1000) {
          handleError();
        }
      }, 100);
    } else {
      success();
    }
    // design && !!designerRef.current ? designerRef.current.submit() : success();
  };

  const handleError = (e) => {
    console.error(e);
    clearInterval(intervalRef.current);
    failure(e);
  };

  React.useImperativeHandle(
    forwardRef,
    () => ({
      submit,
    }),
    [submit]
  );

  const config = miyo?.preview;
  const partnerId = miyo?.partnerConfig?.partner?.id;
  const library = {
    groups: [], //miyo?.partnerConfig?.assetGroups || [],
    assets: [miyo?.asset].filter((v) => !!v), //miyo?.partnerConfig?.assets || [],
    fees: [],
  };

  const handleSubmit = async (e) => {
    setLoading(true);

    try {
      const hash = (s) =>
        `${s.split('').reduce((a, b) => {
          a = (a << 5) - a + b.charCodeAt(0);
          return a & a;
        }, 0)}`;

      const toFile = async (base64, filename) => {
        const split = base64.split(',');
        //const data = split[0].indexOf('base64') >= 0 ? atob(split[1]) : decodeURI(split[1]);
        const mime = split[0].split(':')[1].split(';')[0];

        //const encoder = new TextEncoder();
        // const bytes = encoder.encode(data);
        const buffer = await (await fetch(base64)).arrayBuffer();
        const bytes = [buffer]; // new Uint8Array(buffer);

        return new File(bytes, filename, { type: mime });
      };

      const { faces, files } = e.faces.reduce(
        ({ faces, files }, face) => {
          const hashedProductPreview = face.face + '.' + hash(face.productPreview);
          const hashedPreview = face.face + '.' + hash(face.preview);
          const hashedRender = face.render ? face.face + '.' + hash(face.render) : null;

          const { elements, files: elementFiles } = face.elements.reduce(
            ({ elements, files }, element) => {
              if (element.elementType === 'uploaded') {
                const hashedUrl = hash(element.url);
                return {
                  elements: elements.concat([Object.assign({}, element, { url: hashedUrl })]),
                  files: files.concat([{ hash: hashedUrl, url: element.url }]),
                };
              } else {
                return {
                  elements: elements.concat([element]),
                  files,
                };
              }
            },
            { elements: [], files }
          );

          return {
            faces: faces.concat([
              Object.assign({}, face, {
                elements,
                productPreview: hashedProductPreview,
                preview: hashedPreview,
                render: hashedRender,
              }),
            ]),
            files: files.concat(elementFiles).concat([
              { hash: hashedProductPreview, url: face.productPreview, base64: true },
              { hash: hashedPreview, url: face.preview, base64: true },
              hashedRender && { hash: hashedRender, url: face.render },
            ]).filter(h => !!h),
          };
        },
        { faces: [], files: [] }
      );

      if (e.preview) {
        files.push({
          hash: 'preview.product.all',
          url: e.preview,
          base64: true,
        });
        delete e.preview;
      }

      const formData = new FormData();
      await Promise.all(
        files.map(async ({ hash, url, base64 }) => {
          const file = base64 ? await toFile(url, hash) : new File([url], hash, { type: 'image/x-dxf' });
          formData.append('file', file);
        })
      );
      formData.append(
        'design',
        JSON.stringify(
          Object.assign(e, {
            shopItemId: details.id,
            faces,
            id: design?.id,
            partnerId,
            userAgent: window?.navigator?.userAgent,
          })
        )
      );

      if (design?.copyId) {
        formData.append('copyId', design.copyId);
      }

      const { data } = await axios.post('/custom/design/update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('success', { data });
      success(Object.assign({}, design, { id: data }));
      setLoading(false);
    } catch (e) {
      console.error(e);
      failure(e);
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary onError={handleError} silent>
      {submitting && (
        <React.Suspense>
          <Designer
            ref={designerRef}
            {...{
              visible: false,
              config,
              design,
              library,
              onSubmit: handleSubmit,
              uploadUrl: '',
              previewUrl: '/custom/preview',
              withUpload: false,
              item: {
                typeId: details?.attributes?.typeId,
              },
            }}
          />
        </React.Suspense>
      )}
    </ErrorBoundary>
  );
};

export default memo(forwardRef(DesignExporter));
